import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { CURRENCIES } from 'src/features/payStations/types';
import axios from 'src/lib/axios';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

import type { Parking } from 'src/features/parkingManagement/types/parking';
import type { MutationConfig } from 'src/lib/react-query';
import type { GeneralSmartParkError } from 'src/types';

type FormSchemaValues = z.infer<typeof formSchema>;

export const formSchema = z.object({
    name: z.string({ message: validationMessagesMap.validationErrorRequired }),
    system_id: z.string({ message: validationMessagesMap.validationErrorRequired }),
    country: z.string({ message: validationMessagesMap.validationErrorRequired }),
    city: z.string().optional(),
    address: z.string({ message: validationMessagesMap.validationErrorRequired }),
    default_currency: z.nativeEnum(CURRENCIES).optional(),
    number_of_floors: z.coerce.number().min(0).optional(),
    number_of_spaces: z.coerce.number({ message: validationMessagesMap.validationErrorRequired }).min(0),
    is_public: z.boolean().default(false),
    is_featured: z.boolean().default(false),
    enable_online_payments: z.boolean().default(false),
    enable_online_reservations: z.boolean().default(false),
    serial_id: z.coerce.number().min(0).optional(),
    longitude: z
        .string()
        .refine((val) => {
            const num = Number(val);
            return !isNaN(num) && num >= -180 && num <= 180;
        })
        .optional(),
    latitude: z
        .string()
        .refine((val) => {
            const num = Number(val);
            return !isNaN(num) && num >= -90 && num <= 90;
        })
        .optional(),
});

const RESOURCE = '/parkings/';

type MutationFnType = typeof mutationFn;
interface DTO {
    coordinates?: { type: string; coordinates: number[] };
}

interface UseCreateParking extends MutationConfig<MutationFnType, GeneralSmartParkError> {}

const mutationFn = ({ ...data }: DTO): Promise<Parking> => {
    return axios.post(formatResourcePath(RESOURCE), data);
};

export const useCreateParking = (options: UseCreateParking = {}) =>
    useMutation({
        mutationFn,
        ...options,
    });
export { formSchema as createParkingFormSchema, RESOURCE as PARKINGS_RESOURCE };
export type { FormSchemaValues as CreateParkingFormSchemaValues };
